import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import useUnsavedWarning from '../../../hooks/useUnsavedWarning';
import Button from '../../../components/Button';
import TextInput from '../../../components/TextInput';
import { BarChart3, TrendingUp, BookOpen, Briefcase } from 'lucide-react';
import { getPlatformStates, createPlatformStates, updatePlatformStates } from '../../../api';
import PageLoader from '../../../components/PageLoader';
import Swal from 'sweetalert2';

const defaultForm = {
  _id: null,
  total_trading_volume: '',
  successful_trades: '',
  course_rating: '',
  market_support: '',
  brokers: '',
  comparisons: '',
  rating: '',
  active_traders: '',
  total_courses: '',
  description: '',
};

const validateField = (field, value) => {
  const trimmed = String(value ?? '').trim();
  switch (field) {
    case 'total_trading_volume':
    case 'successful_trades':
    case 'brokers':
    case 'comparisons':
    case 'active_traders':
    case 'total_courses': {
      if (trimmed === '') return 'Required';
      const n = Number(trimmed);
      if (Number.isNaN(n)) return 'Enter a valid number';
      if (n < 0) return 'Must be 0 or more';
      return '';
    }
    case 'course_rating':
    case 'market_support':
    case 'rating':
      if (!trimmed) return 'Required';
      return '';
    case 'description':
      if (!trimmed) return 'Required';
      return '';
    default:
      return '';
  }
};

const AdminCMSPlatformStates = () => {
  const [form, setForm] = useState(defaultForm);
  const [originalForm, setOriginalForm] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const isFormInvalid = useMemo(() => {
    return Object.keys(defaultForm).filter(k => k !== '_id').some(k => {
      const err = validateField(k, form[k]);
      return !!err;
    });
  }, [form]);

  const isChanged = useMemo(() => {
    if (!originalForm) return false;
    return JSON.stringify(form) !== JSON.stringify(originalForm);
  }, [form, originalForm]);
  useUnsavedWarning(isChanged);



  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await getPlatformStates();
      if (data) {
        const stats = {
          _id: data._id || null,
          total_trading_volume: data.total_trading_volume ?? '',
          successful_trades: data.successful_trades ?? '',
          course_rating: data.course_rating ?? '',
          market_support: data.market_support ?? '',
          brokers: data.brokers ?? '',
          comparisons: data.comparisons ?? '',
          rating: data.rating ?? '',
          active_traders: data.active_traders ?? '',
          total_courses: data.total_courses ?? '',
          description: data.description ?? '',
        };
        setForm(stats);
        setOriginalForm(stats);

      } else {
        setForm({ ...defaultForm });
        setOriginalForm({ ...defaultForm });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to load platform states',
        confirmButtonColor: '#38915c',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({
      ...prev,
      [field]: validateField(field, value),
    }));
  };

   const handleSave = async () => {
    if (isFormInvalid) {
      Swal.fire({
        icon: 'warning',
        title: 'Incomplete Fields',
        text: 'Please correct the errors in the form before saving.',
        confirmButtonColor: '#38915c',
      });
      return;
    }

    setSaving(true);

    try {
      const payload = {
        total_trading_volume: Number(form.total_trading_volume),
        successful_trades: Number(form.successful_trades),
        course_rating: String(form.course_rating).trim(),
        market_support: String(form.market_support).trim(),
        brokers: Number(form.brokers),
        comparisons: Number(form.comparisons),
        rating: String(form.rating).trim(),
        active_traders: Number(form.active_traders),
        total_courses: Number(form.total_courses),
        description: String(form.description).trim(),
      };

      let saved;
      if (form._id) {
        saved = await updatePlatformStates(form._id, payload);
      } else {
        saved = await createPlatformStates(payload);
      }

      const updated = {
        ...form,
        _id: saved._id,
        ...saved,
      };
      setForm(updated);
      setOriginalForm(updated);
      Swal.fire({
        icon: 'success',
        title: 'Saved',
        text: 'Platform states updated successfully',
        confirmButtonColor: '#38915c',
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to save platform states',
        confirmButtonColor: '#38915c',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <Link to="/admin/cms" className="text-sm text-primary-600 hover:underline mb-1 inline-block">
            ← CMS Pages
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">Platform States</h1>
          <p className="text-gray-600 text-sm mt-1">
            Manage platform metrics and hero section stats. All fields are required.
          </p>
        </div>
        <Button 
          variant="primary" 
          size="sm" 
          onClick={handleSave} 
          disabled={saving || !isChanged || isFormInvalid}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>



      <div className="space-y-6 max-w-2xl">
        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white">
          <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 size={18} /> Hero section (Active Traders & Courses)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
             <div>
              <TextInput
                label="Active Traders"
                type="number"
                min={0}
                value={form.active_traders}
                onChange={(e) => updateField('active_traders', e.target.value)}
                placeholder="50000"
                error={fieldErrors.active_traders}
                required
              />
            </div>
            <div>
              <TextInput
                label="Total Courses"
                type="number"
                min={0}
                value={form.total_courses}
                onChange={(e) => updateField('total_courses', e.target.value)}
                placeholder="100"
                error={fieldErrors.total_courses}
                required
              />
            </div>
          </div>
        </section>

        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white">
          <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp size={18} /> Bottom stats bar (Trading volume, Trades, Rating, Support)
          </h2>
          <div className="space-y-4">
            <div>
              <TextInput
                label="Total Trading Volume"
                type="number"
                min={0}
                value={form.total_trading_volume}
                onChange={(e) => updateField('total_trading_volume', e.target.value)}
                placeholder="25000000"
                error={fieldErrors.total_trading_volume}
                required
              />
            </div>
            <div>
              <TextInput
                label="Successful Trades"
                type="number"
                min={0}
                value={form.successful_trades}
                onChange={(e) => updateField('successful_trades', e.target.value)}
                placeholder="10000"
                error={fieldErrors.successful_trades}
                required
              />
            </div>
            <div>
              <TextInput
                label="Course Rating"
                value={form.course_rating}
                onChange={(e) => updateField('course_rating', e.target.value)}
                placeholder="4.9★"
                error={fieldErrors.course_rating}
                required
              />
            </div>
            <div>
              <TextInput
                label="Market Support"
                value={form.market_support}
                onChange={(e) => updateField('market_support', e.target.value)}
                placeholder="24/7"
                error={fieldErrors.market_support}
                required
              />
            </div>
          </div>
        </section>

        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white">
          <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Briefcase size={18} /> Compare section (Brokers, Comparisons, Rating, Support)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
             <div>
              <TextInput
                label="Brokers"
                type="number"
                min={0}
                value={form.brokers}
                onChange={(e) => updateField('brokers', e.target.value)}
                placeholder="50"
                error={fieldErrors.brokers}
                required
              />
            </div>
            <div>
              <TextInput
                label="Comparisons"
                type="number"
                min={0}
                value={form.comparisons}
                onChange={(e) => updateField('comparisons', e.target.value)}
                placeholder="100000"
                error={fieldErrors.comparisons}
                required
              />
            </div>
            <div>
              <TextInput
                label="Rating"
                value={form.rating}
                onChange={(e) => updateField('rating', e.target.value)}
                placeholder="4.8★"
                error={fieldErrors.rating}
                required
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Support label uses the same &quot;Market Support&quot; value above.</p>
        </section>

        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white">
          <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BookOpen size={18} /> Description
          </h2>
           <div>
            <textarea
              className={`w-full min-h-[100px] px-3 py-2 border-2 rounded-lg outline-none transition-all ${
                fieldErrors.description 
                  ? 'border-red-500 focus:ring-red-200' 
                  : 'border-gray-200 focus:ring-primary-500 focus:border-primary-500'
              }`}
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Short description for platform states"
            />
            {fieldErrors.description && (
              <p className="text-xs text-red-600 mt-1 font-medium">{fieldErrors.description}</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminCMSPlatformStates;
